import { useState } from 'react'
import styled from 'styled-components'

import { Carat } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { intlPrettyCurrency } from '../../../../lib/utils/currency'
import { usePdpServerSideProps } from '../../../pages/products/ProductPage/PdpServerSidePropsProvider'

export type PriceOptions = { [title: string]: number }

type PriceKey = 'PRODUCT' | 'TEXT_ONLY' | 'ICON_ONLY' | 'ADD_LOGO' | 'ADD_LOGO_SETUP'

export const PriceSummary: React.FC<{
  priceOptions: PriceOptions
}> = ({ priceOptions }) => {
  const [showSummary, setShowSummary] = useState(false)
  const { locale, region } = useLocalization()
  const {
    embroidery: {
      modal: {
        priceSummary: { label, lineItems, finalSaleLabel, totalLabel },
      },
    },
  } = useTranslation()

  const { data } = usePdpServerSideProps()

  const variantPrice = data?.product.variantGroup.variant.price
  const productPrice = (variantPrice?.amount ?? 0) * 100

  const total =
    Object.values(priceOptions).reduce((acc: number, curr) => {
      return acc + curr
    }, 0) + productPrice

  const onExpand = () => {
    setShowSummary(!showSummary)
  }

  const getFormattedPrice = (price: number, units: 'cents' | 'dollars' = 'cents') =>
    intlPrettyCurrency({
      unit: units,
      value: price,
      region: region,
      locale: locale,
    })

  const formattedTotal = getFormattedPrice(total)

  return (
    <Container>
      <Header onClick={onExpand}>
        <Label>
          <CaratIcon $enabled={showSummary} />
          <h2>{label}</h2>
        </Label>
        <PriceTotal>{formattedTotal}</PriceTotal>
      </Header>
      {showSummary && (
        <>
          <LineItems>
            <LineItem>
              <p>{lineItems['PRODUCT']}</p>
              <p>{productPrice && getFormattedPrice(productPrice)}</p>
            </LineItem>
            {Object.entries(priceOptions).map(option => {
              const key = option[0] as PriceKey
              const liLabel = lineItems[key] || ''
              return (
                <LineItem key={option[0] + option[1]}>
                  <p>{liLabel}</p>
                  <p>{getFormattedPrice(option[1])}</p>
                </LineItem>
              )
            })}
          </LineItems>
          <StyledHr />
          <FinalSale>{finalSaleLabel}</FinalSale>
          <Total>
            <Label>{totalLabel}</Label>
            <PriceTotal>{formattedTotal}</PriceTotal>
          </Total>
        </>
      )}
    </Container>
  )
}

const Container = styled.div`
  margin-top: ${o => o.theme.spacing(5)};
  border-radius: ${o => o.theme.spacing(1)};
  background-color: ${o => o.theme.color.fill.subtle};
  font-size: ${o => o.theme.spacing(3)};
  padding: ${o => o.theme.spacing(4)};
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${o => o.theme.spacing(1)};
  font-weight: ${o => o.theme.typography.font.weight[700]};
  cursor: pointer;
  line-height: ${o => o.theme.spacing(6)};
`
const Label = styled.div`
  display: flex;
  align-items: center;
`

const CaratIcon = styled(Carat)<{ $enabled: boolean }>`
  padding: ${o => o.theme.spacing(1.5)};
  transform: ${({ $enabled }) => `rotate(${$enabled ? '-' : ''}90deg)`};
`

const PriceTotal = styled.div``

const LineItems = styled.div`
  font-weight: ${o => o.theme.typography.font.weight[600]};
`

const LineItem = styled.div`
  padding: ${o => o.theme.spacing(2)} 0;
  display: flex;
  justify-content: space-between;
`

const FinalSale = styled.div`
  padding: ${o => o.theme.spacing(2)} 0;
  font-size: ${o => o.theme.spacing(2)};
  font-weight: ${o => o.theme.typography.font.weight[600]};
  text-transform: uppercase;
  color: ${o => o.theme.color.text.critical.on.background};
`

const StyledHr = styled.hr`
  margin: ${o => o.theme.spacing(2)} 0;
  border-top: 0;
  border-color: ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
`

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${o => o.theme.spacing(3)};
  font-weight: ${o => o.theme.typography.font.weight[600]};
`
